/**
 * Search spelling correction from the results themselves. 2026-09-15.
 *
 * Aaron typed "horse riiding boot" and the dropdown filled with hoof boots and
 * over-reach boots: Shopify's own typo tolerance still returned riding boots,
 * but our relevance tier could not see that "riiding" meant "riding", so every
 * product was "missing a word" and the best sellers of ANYTHING with "boot" won.
 *
 * The fix needs no dictionary and no request: the results Shopify returned
 * already contain the right word. For each typed word that appears in NO result
 * title, find the title word closest to it (edit distance 1, or 2 for longer
 * words, same first letter) that appears in several results, and swap it in.
 * "riiding" -> "riding". The caller then searches again with the corrected
 * words and says so ("Showing results for ...").
 *
 * Never corrects a word that does appear in a title, never corrects short words
 * (a 3-letter typo has too many neighbours), never invents a word that fewer
 * than three results carry. Wrong corrections are worse than none.
 *
 *   window.jhrSpell.correct(query, titles) -> corrected query or null
 */
(function () {
  'use strict';

  var STOP = /^(the|and|for|with|from|of|in|on|to|a|an|by)$/;

  function tokens(text) {
    return String(text || '').toLowerCase().replace(/[^a-z0-9' ]+/g, ' ').split(/\s+/).filter(Boolean);
  }

  // Levenshtein with an early exit once the distance cannot come under `max`.
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [];
    for (var j = 0; j <= b.length; j++) prev[j] = j;
    for (var i = 1; i <= a.length; i++) {
      var cur = [i];
      var best = i;
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        cur[k] = Math.min(prev[k] + 1, cur[k - 1] + 1, prev[k - 1] + cost);
        if (cur[k] < best) best = cur[k];
      }
      if (best > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }

  function correct(query, titles) {
    var words = String(query || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length || !titles || !titles.length) return null;
    // word -> number of titles carrying it (a word counted once per title)
    var freq = {};
    for (var t = 0; t < titles.length; t++) {
      var seen = {};
      var toks = tokens(titles[t]);
      for (var i = 0; i < toks.length; i++) {
        if (seen[toks[i]]) continue;
        seen[toks[i]] = true;
        freq[toks[i]] = (freq[toks[i]] || 0) + 1;
      }
    }
    var vocab = Object.keys(freq);
    var changed = false;
    var out = words.map(function (raw) {
      var w = raw.toLowerCase();
      if (STOP.test(w) || freq[w]) return raw;
      // 2026-09-16: a three-letter word that no title carries ("hst") is
      // swapped for a three-letter title word one edit away, but only when
      // there is exactly one such word and five or more results carry it.
      if (w.length === 3) {
        var only = null;
        var count = 0;
        for (var t3 = 0; t3 < vocab.length; t3++) {
          if (vocab[t3].length !== 3 || freq[vocab[t3]] < 5) continue;
          if (distance(w, vocab[t3], 1) <= 1) { count++; only = vocab[t3]; }
        }
        if (count === 1) { changed = true; return only; }
        return raw;
      }
      if (w.length < 4) return raw;
      // The start of a longer known word is not a typo ("boot" while typing
      // "boots"), nor is a plural of one. 2026-09-16: ONLY plural endings count
      // the other way - "premiere" is not a form of "premier", it is the typo.
      for (var v = 0; v < vocab.length; v++) {
        if (vocab[v].indexOf(w) === 0) return raw;
        if (w.indexOf(vocab[v]) === 0 && vocab[v].length >= 3 && /^(s|es)$/.test(w.slice(vocab[v].length))) return raw;
      }
      var max = w.length >= 7 ? 2 : 1;
      var best = null;
      var bestScore = 0;
      for (var c = 0; c < vocab.length; c++) {
        var cand = vocab[c];
        if (freq[cand] < 3 || cand.length < 3) continue;
        if (cand.length === 3 && freq[cand] < 5) continue;   // 'hast' -> 'hat' only when hats are plainly the results
        // a different first letter is allowed only for a one-letter slip on a
        // word many results carry ("hires" -> "shires"; 2026-09-16)
        if (cand.charAt(0) !== w.charAt(0) && freq[cand] < 5) continue;
        var d = distance(w, cand, max);
        if (d > max) continue;
        if (cand.charAt(0) !== w.charAt(0) && d > 1) continue;
        // closer wins; among equals the word more results carry
        var score = (max + 1 - d) * 1000 + freq[cand];
        if (score > bestScore) { bestScore = score; best = cand; }
      }
      if (!best) return raw;
      changed = true;
      return best;
    });
    return changed ? out.join(' ') : null;
  }

  // ---------------------------------------------------------------------------------------------
  // RESCUE (2026-09-29). Aaron: "Dobson and Horrell is Dodson and Horrell ... it should have really
  // picked up on that", "6ft3 should be bringing up the rugs" - and "it should just be doing it".
  // When a search finds NOTHING, or only products that carry none of the typed words, there are no
  // titles to learn the right word from, so correct() above cannot help. Two more things are tried,
  // in this order, and the caller says so exactly like a spelling fix ("Showing results for ..."):
  //   1. a size written another way, in the shop's way: "6ft3", "6 ft 3", "6 foot 3in", 6'3" -> 6'3
  //   2. a brand we sell, typed wrong or half typed: "dobson and ho" -> Dodson & Horrell,
  //      "likif" -> Likit (the makers list the nightly search job keeps in search_signals.vendors,
  //      fetched once, only when a search has failed)
  // Nothing is filtered and nothing is added (Aaron, 16 Sep: "just search what people put in"): the
  // words they typed are written the shop's way, and searched.
  // ---------------------------------------------------------------------------------------------

  // "6ft3" / "6 ft 3" / "6 foot 3 in" / 6'3" -> 6'3 (inches 0-11 only, so "5 ft 30 g" keeps its 30g)
  function sizeFix(query) {
    var q = String(query || '');
    var out = q.replace(/(^|\s)(\d)\s*(?:ft|foot|feet|')\s*(1[01]|\d)?\s*(?:"|''|in|inch|inches)?(?=\s|$)/gi,
      function (m, lead, ft, ins) { return lead + ft + "'" + (ins || '0'); });
    return out !== q ? out : null;
  }

  // True when the results carry none of the typed words: Shopify's loose matching found "something",
  // not the thing ("dobson and ho" brought a solar stable light and a sheep supplement).
  function unrelated(query, titles) {
    if (!titles || !titles.length) return true;
    var words = tokens(query).filter(function (w) { return w.length >= 4 && !STOP.test(w) && !/\d/.test(w); });
    if (!words.length) return false;
    var text = ' ' + titles.map(function (t) { return tokens(t).join(' '); }).join(' ') + ' ';
    return !words.some(function (w) {
      var stem = w.length >= 4 ? w.replace(/(es|s)$/, '') : w;         // "rugs" is carried by "rug"
      return text.indexOf(' ' + w) >= 0 || text.indexOf(' ' + stem) >= 0;
    });
  }

  // "and" is left out on both sides, so "dodson horell" and "dobson and ho" both reach "Dodson & Horrell"
  function norm(s) {
    return String(s || '').toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9' ]+/g, ' ')
      .split(/\s+/).filter(function (w) { return w && w !== 'and'; });
  }
  // Levenshtein counting two swapped letters as ONE slip ("shiers" -> "shires")
  function slips(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var d = [];
    for (var i = 0; i <= a.length; i++) { d[i] = [i]; }
    for (var j = 0; j <= b.length; j++) d[0][j] = j;
    for (i = 1; i <= a.length; i++) {
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  }
  // Brand names that are ordinary words: never "corrected" into ("foot" must not become "Fort")
  var PLAIN = /^(fort|fast|over|express|standard|dual|groom|hunter|champion|supreme|george|repair|stainless|vegetable|shark|sundry|embossed|rock|roma|barrier|companion|cottage|dazzling|flutter|heritage|optimum|podium|rondo|sheila|shep|shire|lola|hugo|milano|monsoon|uptite|unknown|none|null|welby|gallop)$/;
  // 0 = the same word, 0.5 = the last word still being typed ("ho" of "horrell", "equetec" of "equetech"),
  // 1-2 = a typo, 99 = no
  function tokenFits(typed, real, isLast, oneWord) {
    if (typed === real) return 0;
    if (isLast && real.indexOf(typed) === 0) {
      // finishing a word: fine inside a longer brand name ("ho" -> Horrell); for a one-word brand only when
      // it is nearly all typed ("equetec" -> Equetech, never "rug" -> Rugsafe)
      if (!oneWord && typed.length >= 2) return 0.5;
      if (oneWord && typed.length >= 5 && typed.length >= real.length * 0.75) return 0.5;
    }
    if (typed.length < 4 || typed.charAt(0) !== real.charAt(0)) return 99;
    var max = typed.length >= 7 ? 2 : 1;
    var d = slips(typed, real, max);
    return d <= max ? d : 99;
  }
  function brandFix(query, vendors) {
    var q = norm(query);
    if (!q.length || !vendors) return null;
    var names = Object.keys(vendors).filter(function (name) {
      var v = norm(name);
      var joined = v.join('');
      // a one-product "maker" is often a typo itself; very short or number names are too easy to hit
      return (vendors[name] || 0) >= 2 && v.length && joined.length >= 4 && /[a-z]{3}/.test(joined) && !(v.length === 1 && PLAIN.test(v[0]));
    });
    // a brand we sell already spelled right: the words are fine, leave them ("horseware rambo" stays)
    var hasExact = names.some(function (name) {
      var v = norm(name);
      for (var i = 0; i + v.length <= q.length; i++) {
        if (v.every(function (w, j) { return q[i + j] === w; })) return true;
      }
      return false;
    });
    if (hasExact) return null;
    var best = null;
    names.forEach(function (name) {
      var v = norm(name);
      for (var i = 0; i + v.length <= q.length; i++) {
        var cost = 0;
        for (var j = 0; j < v.length && cost < 99; j++) cost += tokenFits(q[i + j], v[j], i + j === q.length - 1, v.length === 1);
        if (cost === 0 || cost > 2) continue;
        var score = cost - v.length * 0.1;                          // closer first; a longer name wins a tie
        if (!best || score < best.score) best = { score: score, i: i, n: v.length, name: name };
      }
    });
    if (!best) return null;
    q.splice(best.i, best.n, best.name);
    return q.join(' ');
  }

  var vendorsP = null;
  function loadVendors() {
    if (vendorsP) return vendorsP;
    var base = (window.theme && window.theme.routes && window.theme.routes.base) || '';
    vendorsP = fetch(base + '/search?section_id=jhr-search-vocab', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var m = /<script[^>]*id="jhr-search-vocab"[^>]*>([\s\S]*?)<\/script>/.exec(html);
        return m ? JSON.parse(m[1]) || {} : {};
      })
      .catch(function () { vendorsP = null; return {}; });
    return vendorsP;
  }

  // Promise of the better words, or null. Call only when the search found nothing, or nothing related.
  function rescue(query) {
    var s = sizeFix(query);
    if (s) return Promise.resolve(s);
    return loadVendors().then(function (v) { return brandFix(query, v); });
  }

  window.jhrSpell = { correct: correct, distance: distance, unrelated: unrelated, rescue: rescue, sizeFix: sizeFix, brandFix: brandFix };
})();
